import { Box, Checkbox, Chip, IconButton, Typography, alpha } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PermissionTreeNode as PermissionTreeNodeType } from 'types/apis/rbacInterfaces';
import { cascadeGroupToggle, countSubtreeFeatures } from './PermissionTreeNode';

const pendingRowBg = { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.08) };
const chipSx = { height: 18, fontSize: 11 } as const;

interface GroupAssignRowProps {
  node: PermissionTreeNodeType;
  depth: number;
  pending: Map<string, boolean>;
  collapsed: Set<number>;
  ancestorPendingGrant?: boolean;
  ancestorPendingRevokeIds?: Set<number>;
  onToggleExpand: (id: number) => void;
  onToggleGroup: (groupId: number, grant: boolean, serverGranted: boolean) => void;
}

const EMPTY_REVOKE_IDS = new Set<number>();

/**
 * Group-row-only variant of PermissionTreeNode: same tri-state grant checkbox and pending/chip
 * conventions, but never renders a group's individual features — this feeds the Access Groups
 * assignment tab, where the point is "grant the bundle," not "pick leaf features" (that stays
 * in API Permissions).
 */
export const GroupAssignRow = ({
  node,
  depth,
  pending,
  collapsed,
  ancestorPendingGrant = false,
  ancestorPendingRevokeIds = EMPTY_REVOKE_IDS,
  onToggleExpand,
  onToggleGroup,
}: GroupAssignRowProps) => {
  const hasChildren = (node.children?.length || 0) > 0;
  const expanded = !collapsed.has(node.id);

  const groupOverride = pending.get(`g:${node.id}`);
  const serverGranted = node.grant_state === 'granted';
  const selfPendingGrant = ancestorPendingGrant || groupOverride === true;
  const childRevokeIds = groupOverride === false
    ? new Set([...ancestorPendingRevokeIds, node.id])
    : ancestorPendingRevokeIds;

  const counts = countSubtreeFeatures(node, pending, ancestorPendingGrant, ancestorPendingRevokeIds);
  // Same tri-state aggregate as PermissionTreeNode: the checkbox reflects the
  // subtree (all/some/none granted), not just the group's own grant.
  const selfChecked = groupOverride !== undefined ? groupOverride : ancestorPendingGrant || serverGranted;
  const checked = counts.total > 0 ? counts.granted === counts.total : selfChecked;
  const indeterminate = counts.granted > 0 && counts.granted < counts.total;
  const groupIsPending = groupOverride !== undefined;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: depth * 2,
          py: 0.25,
          borderRadius: 1,
          ...(groupIsPending ? pendingRowBg : {}),
        }}
      >
        <IconButton
          size="small"
          onClick={() => onToggleExpand(node.id)}
          disabled={!hasChildren}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          disabled={ancestorPendingGrant}
          onChange={(e) => cascadeGroupToggle(node, e.target.checked, onToggleGroup)}
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{node.name}</Typography>
        {counts.total > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${counts.granted}/${counts.total} actions`}
            sx={{ ...chipSx, color: 'text.secondary' }}
          />
        )}
        {groupIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
      </Box>

      {expanded && node.children?.map((child) => (
        <GroupAssignRow
          key={child.id}
          node={child}
          depth={depth + 1}
          pending={pending}
          collapsed={collapsed}
          ancestorPendingGrant={selfPendingGrant}
          ancestorPendingRevokeIds={childRevokeIds}
          onToggleExpand={onToggleExpand}
          onToggleGroup={onToggleGroup}
        />
      ))}
    </Box>
  );
};
